import { ArrowLeft, History } from "lucide-react";
import { useState } from "react";
import { Link } from "react-router-dom";
import { cx, Empty, ErrorNote, Spinner } from "../../components/ui";
import { useApi } from "../../lib/api";
import { keyLabel, STATUS_LABEL, STATUS_TONE, when, type RunSummary } from "./types";

/** Status and History (design §8.6): open runs, and finished ones with what they did. */
export default function JaRuns() {
  const [tab, setTab] = useState<"open" | "ended">("open");
  const { data, error, loading } = useApi<{ runs: RunSummary[] }>(`/apps/ja/runs?open=${tab === "open"}`);
  return (
    <div>
      <Link to="/apps/ja" className="inline-flex items-center gap-1 text-sm text-accent-text hover:underline mb-3">
        <ArrowLeft className="size-4" aria-hidden /> Automations
      </Link>
      <h1 className="text-2xl font-bold text-ink mb-4">Runs</h1>
      <div className="flex gap-1 mb-4 border-b border-line">
        {(["open", "ended"] as const).map((t) => (
          <button key={t} type="button" onClick={() => setTab(t)} className={cx("px-3 py-2 text-sm -mb-px border-b-2", tab === t ? "border-accent text-ink font-medium" : "border-transparent text-muted hover:text-ink")}>
            {t === "open" ? "Open" : "History"}
          </button>
        ))}
      </div>
      {error && <ErrorNote>{error}</ErrorNote>}
      {loading && !data ? (
        <Spinner />
      ) : !data?.runs.length ? (
        <Empty icon={<History className="size-8" />} title={tab === "open" ? "No open runs" : "No finished runs yet"} />
      ) : (
        <div className="overflow-x-auto rounded-xl border border-line bg-surface">
          <table className="w-full text-sm">
            <thead className="text-left text-muted border-b border-line">
              <tr>
                <th className="px-4 py-2 font-medium">Automation</th>
                <th className="px-4 py-2 font-medium">Run key</th>
                <th className="px-4 py-2 font-medium">Status</th>
                <th className="px-4 py-2 font-medium">Started</th>
                <th className="px-4 py-2 font-medium">{tab === "open" ? "Last change" : "Ended"}</th>
              </tr>
            </thead>
            <tbody>
              {data.runs.map((r) => (
                <tr key={r.runId} className="border-b border-line last:border-0">
                  <td className="px-4 py-3">
                    <Link to={`/apps/ja/runs/${r.runId}`} className="font-medium text-accent-text hover:underline">
                      {r.name}
                    </Link>
                    <div className="text-xs text-muted">
                      {r.type} v{r.version} · run {r.seq}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-ink-2">{keyLabel(r.runKey)}</td>
                  <td className="px-4 py-3">
                    <span className={cx("text-xs font-semibold rounded-full px-2 py-0.5", STATUS_TONE[r.status])}>{STATUS_LABEL[r.status]}</span>
                    {r.open && r.current && <div className="text-xs text-muted mt-1">{r.current}</div>}
                  </td>
                  <td className="px-4 py-3 text-xs text-muted">
                    {when(r.startedAt)}
                    <div>{r.startedBy}</div>
                  </td>
                  <td className="px-4 py-3 text-xs text-muted">{when(tab === "open" ? r.updatedAt : r.endedAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
